'use client';

import {
  Wifi,
  UtensilsCrossed,
  Waves,
  Car,
  Dumbbell,
  Wind,
  Coffee,
  Shield,
  Tv,
  Bath,
  Building2,
  CalendarCheck,
  Users,
  Star,
  BarChart3,
  Zap,
  Globe,
  Headphones,
  Puzzle,
} from 'lucide-react';
import type {
  Hotel,
  FeaturedHotelCard,
  HotelReview,
  HotelAmenity,
  MapHotel,
  PriceRange,
  CompareHotel,
  HeroStatItem,
  Feature,
  Testimonial,
  PricingPlan,
  PopularLocation,
} from '../types/hotels.types';

/* ── Haversine Distance (km) ───────────────────────────────────────── */

const EARTH_RADIUS_KM = 6371;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineDistanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/* ── India Map Projection ──────────────────────────────────────────── */

const INDIA_BOUNDS = {
  minLat: 6,
  maxLat: 36,
  minLng: 68,
  maxLng: 98,
};

const SVG_WIDTH = 1000;
const SVG_HEIGHT = 562;

export function projectToSvg(lat: number, lng: number): { x: number; y: number } {
  const x = ((lng - INDIA_BOUNDS.minLng) / (INDIA_BOUNDS.maxLng - INDIA_BOUNDS.minLng)) * SVG_WIDTH;
  const y = ((INDIA_BOUNDS.maxLat - lat) / (INDIA_BOUNDS.maxLat - INDIA_BOUNDS.minLat)) * SVG_HEIGHT;
  return { x, y };
}

export function radiusToSvgPixels(km: number): number {
  const degPerKm = 1 / 111;
  const degSpan = km * degPerKm;
  return (degSpan / (INDIA_BOUNDS.maxLat - INDIA_BOUNDS.minLat)) * SVG_HEIGHT;
}

/* ── Popular Search Locations ──────────────────────────────────────── */

export const POPULAR_LOCATIONS: PopularLocation[] = [
  { slug: 'mumbai', name: 'Mumbai', latitude: 19.0760, longitude: 72.8777 },
  { slug: 'delhi', name: 'New Delhi', latitude: 28.6139, longitude: 77.2090 },
  { slug: 'bangalore', name: 'Bangalore', latitude: 12.9716, longitude: 77.5946 },
  { slug: 'goa', name: 'Goa', latitude: 15.2993, longitude: 74.1240 },
  { slug: 'jaipur', name: 'Jaipur', latitude: 26.9124, longitude: 75.7873 },
  { slug: 'udaipur', name: 'Udaipur', latitude: 24.5854, longitude: 73.7125 },
  { slug: 'manali', name: 'Manali', latitude: 32.2396, longitude: 77.1887 },
  { slug: 'chennai', name: 'Chennai', latitude: 13.0827, longitude: 80.2707 },
  { slug: 'kolkata', name: 'Kolkata', latitude: 22.5726, longitude: 88.3639 },
  { slug: 'hyderabad', name: 'Hyderabad', latitude: 17.3850, longitude: 78.4867 },
  { slug: 'kochi', name: 'Kochi', latitude: 9.9312, longitude: 76.2673 },
  { slug: 'shimla', name: 'Shimla', latitude: 31.1048, longitude: 77.1734 },
];

/* ── Radius Options ────────────────────────────────────────────────── */

export const RADIUS_OPTIONS = [
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 200, label: '200 km' },
  { value: 500, label: '500 km' },
] as const;

export const DEFAULT_RADIUS_KM = 25;

/* ── Featured Hotels ─────────────────────────────────────────────────── */

export const FEATURED_HOTELS: FeaturedHotelCard[] = [
  {
    slug: 'the-grand-palace', name: 'The Grand Palace',
    location: 'Mumbai, Maharashtra', latitude: 19.0760, longitude: 72.8777,
    stars: 5, price: 108, oldPrice: 150,
    image: 'https://picsum.photos/seed/grand-palace/800/600',
    amenities: [{ icon: Wifi, label: 'Wi-Fi' }, { icon: UtensilsCrossed, label: 'Dining' }, { icon: Waves, label: 'Pool' }],
    isFeatured: true, badge: 'Featured', tagline: 'Luxury stays starting ₹4,999/night', promotionalPrice: 4999,
  },
  {
    slug: 'seaside-resort', name: 'Seaside Resort',
    location: 'Calangute, Goa', latitude: 15.5449, longitude: 73.7554,
    stars: 4, price: 78, oldPrice: 114,
    image: 'https://picsum.photos/seed/seaside-resort/800/600',
    amenities: [{ icon: Waves, label: 'Beach' }, { icon: Wifi, label: 'Wi-Fi' }, { icon: UtensilsCrossed, label: 'Dining' }],
    isSponsored: true, badge: 'Sponsored', tagline: 'Beachfront luxury at unbeatable prices',
  },
  {
    slug: 'heritage-haveli', name: 'Heritage Haveli',
    location: 'Jaipur, Rajasthan', latitude: 26.9124, longitude: 75.7873,
    stars: 5, price: 90, oldPrice: 132,
    image: 'https://picsum.photos/seed/heritage-haveli/800/600',
    amenities: [{ icon: UtensilsCrossed, label: 'Dining' }, { icon: Wifi, label: 'Wi-Fi' }, { icon: Waves, label: 'Pool' }],
    isFeatured: true, badge: 'Featured',
  },
  {
    slug: 'lakeside-retreat', name: 'Lakeside Retreat',
    location: 'Udaipur, Rajasthan', latitude: 24.5854, longitude: 73.7125,
    stars: 5, price: 114, oldPrice: 168,
    image: 'https://picsum.photos/seed/lakeside-retreat/800/600',
    amenities: [{ icon: Waves, label: 'Lake View' }, { icon: UtensilsCrossed, label: 'Dining' }, { icon: Wifi, label: 'Wi-Fi' }],
  },
];

/* ── Hotel Detail Amenities ──────────────────────────────────────────── */

export const HOTEL_AMENITIES: HotelAmenity[] = [
  { icon: Wifi, label: 'High-Speed Wi-Fi' },
  { icon: Car, label: 'Valet Parking' },
  { icon: UtensilsCrossed, label: 'Fine Dining' },
  { icon: Dumbbell, label: 'Fitness Centre' },
  { icon: Waves, label: 'Infinity Pool' },
  { icon: Wind, label: 'Climate Control' },
  { icon: Coffee, label: 'Lounge & Bar' },
  { icon: Shield, label: '24/7 Security' },
  { icon: Tv, label: 'Smart TV' },
  { icon: Bath, label: 'Luxury Bath' },
];

/* ── Hotel Detail Reviews ────────────────────────────────────────────── */

export const HOTEL_REVIEWS: HotelReview[] = [
  { initials: 'AK', name: 'Arjun Kapoor', date: 'February 2026', rating: 5, verified: true,
    text: 'Absolutely spectacular stay. The ocean-facing suite was immaculate, and the staff went above and beyond to make our anniversary celebration unforgettable. The rooftop dining experience at sunset was worth every penny.' },
  { initials: 'SM', name: 'Sneha Menon', date: 'January 2026', rating: 4, verified: true,
    text: 'Beautiful property with world-class amenities. The spa treatment was one of the best I have ever had. Only minor issue was the wait time at the restaurant during peak breakfast hours, but the quality of food more than compensated.' },
  { initials: 'RD', name: 'Rahul Deshmukh', date: 'December 2025', rating: 5, verified: true,
    text: 'This is what five-star hospitality looks like. From the moment we arrived, the concierge team ensured everything was flawless. The room was pristine, the views breathtaking, and the in-room dining menu rivals some of Mumbai\'s best restaurants.' },
  { initials: 'PJ', name: 'Priya Joshi', date: 'March 2026', rating: 5, verified: true,
    text: 'Perfect weekend getaway. The infinity pool overlooking the city skyline is mesmerizing, especially during golden hour. Staff remembered our preferences from our last visit — truly personalized service.' },
  { initials: 'VK', name: 'Vikram Kumar', date: 'November 2025', rating: 3, verified: false,
    text: 'Decent stay overall. The room was clean and well-maintained. However, the check-in process took longer than expected and the gym equipment could use some upgrading. Good location though.' },
  { initials: 'NS', name: 'Nisha Sharma', date: 'January 2026', rating: 4, verified: true,
    text: 'Loved the heritage architecture blended with modern comforts. The courtyard dining under the stars was magical. Room service was prompt and the concierge helped us plan the perfect Jaipur itinerary.' },
  { initials: 'AT', name: 'Aditya Thakur', date: 'October 2025', rating: 2, verified: false,
    text: 'Expected more for the price point. The room was smaller than advertised and the air conditioning was inconsistent. Breakfast variety was limited. The location is convenient but the property needs renovation.' },
  { initials: 'MR', name: 'Meera Reddy', date: 'March 2026', rating: 5, verified: true,
    text: 'Our third stay here and it keeps getting better. The new lakeside terrace restaurant is phenomenal. The children\'s activity area kept our kids entertained all day. Already planning our next visit.' },
];

/* ── Amenity Filter Options ───────────────────────────────────────────── */

export const AMENITY_FILTER_OPTIONS = [
  'Wi-Fi', 'Restaurant', 'Pool', 'Parking', 'Spa', 'Gym',
] as const;

/* ── Rating Filter Options ────────────────────────────────────────────── */

export const RATING_FILTER_OPTIONS = [
  { value: 0, label: 'Any Rating' },
  { value: 4.0, label: '4.0+ Good' },
  { value: 4.5, label: '4.5+ Very Good' },
  { value: 4.7, label: '4.7+ Excellent' },
] as const;

/* ── Trending Destinations ────────────────────────────────────────────── */

export const TRENDING_DESTINATIONS = [
  { slug: 'mumbai', name: 'Mumbai' },
  { slug: 'goa', name: 'Goa' },
  { slug: 'jaipur', name: 'Jaipur' },
  { slug: 'udaipur', name: 'Udaipur' },
  { slug: 'manali', name: 'Manali' },
  { slug: 'delhi', name: 'Delhi' },
] as const;

/* ── Hotel Detail Gallery ────────────────────────────────────────────── */

export const GALLERY_IMAGES = [
  'https://picsum.photos/seed/hotel-exterior/1200/800',
  'https://picsum.photos/seed/hotel-room/600/400',
  'https://picsum.photos/seed/hotel-pool/600/400',
];

export const MORE_GALLERY_IMAGES = [
  'https://picsum.photos/seed/hotel-lobby/600/400',
  'https://picsum.photos/seed/hotel-restaurant/600/400',
  'https://picsum.photos/seed/hotel-spa/600/400',
  'https://picsum.photos/seed/hotel-suite/600/400',
  'https://picsum.photos/seed/hotel-bar/600/400',
  'https://picsum.photos/seed/hotel-gym/600/400',
];

/* ── Hotel Map Data ──────────────────────────────────────────────────── */

export const MAP_HOTELS: MapHotel[] = [
  { id: 'h1', name: 'The Grand Regency', city: 'Mumbai', price: 8500, rating: 4.8, latitude: 19.0760, longitude: 72.8777 },
  { id: 'h2', name: 'Coral Bay Resort', city: 'Goa', price: 12000, rating: 4.6, latitude: 15.2993, longitude: 74.1240 },
  { id: 'h3', name: 'Royal Palace Jaipur', city: 'Jaipur', price: 15000, rating: 4.9, latitude: 26.9124, longitude: 75.7873 },
  { id: 'h4', name: 'Imperial Heights', city: 'Delhi', price: 6500, rating: 4.3, latitude: 28.6139, longitude: 77.2090 },
  { id: 'h5', name: 'Lakeside Udaipur', city: 'Udaipur', price: 9800, rating: 4.7, latitude: 24.5854, longitude: 73.7125 },
  { id: 'h6', name: 'Coastal Retreat Kerala', city: 'Kerala', price: 7200, rating: 4.5, latitude: 9.9312, longitude: 76.2673 },
];

export const PRICE_RANGES: PriceRange[] = [
  { label: 'Under \u20B98,000', color: '#0D9E99', min: 0, max: 7999 },
  { label: '\u20B98,000 - \u20B912,000', color: '#C8913A', min: 8000, max: 12000 },
  { label: 'Above \u20B912,000', color: '#E0454A', min: 12001, max: Infinity },
];

/* ── Hotel Compare Data ──────────────────────────────────────────────── */

export const COMPARE_HOTELS: CompareHotel[] = [
  {
    id: 'h1', name: 'The Grand Regency', city: 'Mumbai', stars: 5,
    price: 8500, rating: 4.8, reviews: 1243,
    amenities: ['Wi-Fi', 'Pool', 'Spa', 'Restaurant', 'Gym'],
    roomTypes: ['Deluxe', 'Suite', 'Presidential'],
  },
  {
    id: 'h2', name: 'Coral Bay Resort', city: 'Goa', stars: 5,
    price: 12000, rating: 4.6, reviews: 876,
    amenities: ['Wi-Fi', 'Pool', 'Beach', 'Restaurant', 'Bar'],
    roomTypes: ['Standard', 'Deluxe', 'Villa'],
  },
  {
    id: 'h3', name: 'Royal Palace Jaipur', city: 'Jaipur', stars: 5,
    price: 15000, rating: 4.9, reviews: 654,
    amenities: ['Wi-Fi', 'Pool', 'Spa', 'Restaurant', 'Heritage Tour'],
    roomTypes: ['Heritage', 'Royal Suite', 'Maharaja Suite'],
  },
];

/* ── Hotel Search Results Data ───────────────────────────────────────── */

export const SEARCH_HOTELS: Hotel[] = [
  {
    id: 'grand-palace', name: 'The Grand Palace', location: 'Mumbai, Maharashtra',
    latitude: 19.0760, longitude: 72.8777,
    rating: 4.8, reviews: 342, price: 3500, type: 'Hotel', stars: 5,
    image: 'https://picsum.photos/seed/grand-palace/800/600',
    amenities: ['Wi-Fi', 'Restaurant', 'Pool', 'Parking', 'Spa', 'Gym'],
    roomTypes: ['Deluxe', 'Suite', 'Presidential'],
    availability: 3, lastBooked: '2 hours ago', freeCancellation: true,
    phone: '+91 22 6789 0123', email: 'reservations@grandpalace.in', address: '123 Marine Drive, Mumbai 400020',
  },
  {
    id: 'seaside-resort', name: 'Seaside Resort & Spa', location: 'Goa, India',
    latitude: 15.5449, longitude: 73.7554,
    rating: 4.6, reviews: 218, price: 5200, type: 'Resort', stars: 5,
    image: 'https://picsum.photos/seed/seaside-resort/800/600',
    amenities: ['Wi-Fi', 'Pool', 'Restaurant', 'Spa'],
    roomTypes: ['Standard', 'Deluxe', 'Villa'],
    availability: 7, lastBooked: '5 hours ago', freeCancellation: true,
    phone: '+91 832 567 8901', email: 'book@seasideresort.in', address: 'Calangute Beach Road, Goa 403516',
  },
  {
    id: 'mountain-view', name: 'Mountain View Lodge', location: 'Manali, Himachal Pradesh',
    latitude: 32.2396, longitude: 77.1887,
    rating: 4.5, reviews: 156, price: 4100, type: 'Boutique', stars: 4,
    image: 'https://picsum.photos/seed/mountain-lodge/800/600',
    amenities: ['Wi-Fi', 'Restaurant', 'Parking'],
    roomTypes: ['Standard', 'Superior', 'Suite'],
    availability: 2, lastBooked: '1 day ago', freeCancellation: true,
    phone: '+91 1902 345 678', email: 'stay@mountainviewlodge.in', address: 'Old Manali Road, Manali 175131',
  },
  {
    id: 'urban-suites', name: 'Urban Suites', location: 'Bangalore, Karnataka',
    latitude: 12.9716, longitude: 77.5946,
    rating: 4.3, reviews: 289, price: 2800, type: 'Hotel', stars: 4,
    image: 'https://picsum.photos/seed/urban-suites/800/600',
    amenities: ['Wi-Fi', 'Parking', 'Gym'],
    roomTypes: ['Studio', 'Executive', 'Penthouse'],
    availability: 9, lastBooked: '30 minutes ago', freeCancellation: false,
    phone: '+91 80 4567 8901', email: 'hello@urbansuites.in', address: 'MG Road, Bangalore 560001',
  },
  {
    id: 'heritage-haveli', name: 'Heritage Haveli', location: 'Jaipur, Rajasthan',
    latitude: 26.9124, longitude: 75.7873,
    rating: 4.7, reviews: 412, price: 6500, type: 'Heritage', stars: 5,
    image: 'https://picsum.photos/seed/heritage-haveli/800/600',
    amenities: ['Wi-Fi', 'Restaurant', 'Pool', 'Parking', 'Spa'],
    roomTypes: ['Heritage', 'Royal Suite', 'Maharaja Suite'],
    availability: 1, lastBooked: '4 hours ago', freeCancellation: true,
    phone: '+91 141 678 9012', email: 'royal@heritagehaveli.in', address: 'Nahargarh Road, Jaipur 302001',
  },
  {
    id: 'lakeside-retreat', name: 'Lakeside Retreat', location: 'Udaipur, Rajasthan',
    latitude: 24.5854, longitude: 73.7125,
    rating: 4.9, reviews: 531, price: 8200, type: 'Resort', stars: 5,
    image: 'https://picsum.photos/seed/lakeside-retreat/800/600',
    amenities: ['Wi-Fi', 'Pool', 'Restaurant', 'Parking', 'Spa', 'Gym'],
    roomTypes: ['Lakeside Room', 'Premium Suite', 'Royal Villa'],
    availability: 4, lastBooked: '1 hour ago', freeCancellation: true,
    phone: '+91 294 345 6789', email: 'lake@lakesideretreat.in', address: 'Lake Pichola, Udaipur 313001',
  },
];

/* ── Hero Section Stats ──────────────────────────────────────────────── */

export const HERO_STATS: HeroStatItem[] = [
  { icon: Building2, value: 500, suffix: '+', label: 'Hotels Worldwide', decimals: 0 },
  { icon: CalendarCheck, value: 50, suffix: 'K+', label: 'Bookings Processed', decimals: 0 },
  { icon: Users, value: 15, suffix: 'K+', label: 'Happy Guests', decimals: 0 },
  { icon: Star, value: 4.9, suffix: '', label: 'Rating', decimals: 1 },
];

/* ── Why Choose Us Features ──────────────────────────────────────────── */

export const FEATURES: Feature[] = [
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-grade encryption, SOC 2 compliance, and role-based access control for complete data protection.',
  },
  {
    icon: BarChart3,
    title: 'Revenue Analytics',
    description: 'Real-time dashboards, RevPAR tracking, and AI-powered pricing recommendations to maximize revenue.',
  },
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Sub-second page loads, real-time updates, and instant sync across all devices and properties.',
  },
  {
    icon: Globe,
    title: 'Multi-Property',
    description: 'Manage unlimited hotels from a single dashboard with consolidated reporting and brand controls.',
  },
  {
    icon: Headphones,
    title: '24/7 Support',
    description: 'Dedicated account managers, live chat, and phone support with 99.9% uptime SLA.',
  },
  {
    icon: Puzzle,
    title: '200+ Integrations',
    description: 'Connect with OTAs, payment gateways, POS systems, and accounting software seamlessly.',
  },
];

/* ── Testimonials ────────────────────────────────────────────────────── */

export const TESTIMONIALS: Testimonial[] = [
  {
    quote: 'HotelStack transformed our operations. Occupancy went up 23% in the first quarter and RevPAR hit an all-time high.',
    name: 'James Whitfield',
    role: 'General Manager',
    hotel: 'The Greenwich Inn, London',
    initials: 'JW',
  },
  {
    quote: 'We switched from Cloudbeds and saw a 30% RevPAR increase within 90 days. The channel manager is outstanding.',
    name: 'Ahmed Al-Rashid',
    role: 'Director of Revenue',
    hotel: 'Marina Bay Suites, Dubai',
    initials: 'AR',
  },
  {
    quote: 'The best PMS I have used in 20 years of hospitality. Analytics dashboard alone pays for itself every month.',
    name: 'Somchai Pattana',
    role: 'Owner',
    hotel: 'Siam Heritage, Bangkok',
    initials: 'SP',
  },
  {
    quote: 'Managing 12 properties from one dashboard changed everything. Cross-property reporting saves us days every month.',
    name: 'David Chen',
    role: 'VP Operations',
    hotel: 'Pacific Resort Group, Sydney',
    initials: 'DC',
  },
  {
    quote: 'Switching from our legacy system was seamless. The support team was incredible throughout onboarding.',
    name: 'Maria Gonzalez',
    role: 'Hotel Director',
    hotel: 'Casa del Sol, Barcelona',
    initials: 'MG',
  },
  {
    quote: 'The F&B module with POS integration solved our biggest headache. Zero unbilled charges since we deployed.',
    name: 'Rajesh Kapoor',
    role: 'General Manager',
    hotel: 'The Grand Palace, Mumbai',
    initials: 'RK',
  },
  {
    quote: 'In the relentlessly competitive NYC market, HotelStack gave us the edge we needed. Our front-desk efficiency doubled and guest satisfaction scores climbed 18% in just two months.',
    name: 'Catherine Ellis',
    role: 'Director of Operations',
    hotel: 'The Gramercy Park Hotel, New York',
    initials: 'CE',
  },
];

/* ── Pricing Plans ───────────────────────────────────────────────────── */

export const PRICING_PLANS: PricingPlan[] = [
  {
    name: 'Starter', price: '49', tagline: 'Perfect for small hotels',
    features: ['Up to 30 rooms', 'Basic analytics', 'Email support', '1 user', 'Standard reports'],
    regionalPricing: {
      USD: { symbol: '$', amount: '49', code: 'USD' }, EUR: { symbol: '\u20AC', amount: '45', code: 'EUR' },
      GBP: { symbol: '\u00A3', amount: '39', code: 'GBP' }, AED: { symbol: 'AED', amount: '179', code: 'AED' },
      INR: { symbol: '\u20B9', amount: '3,999', code: 'INR' }, THB: { symbol: '\u0E3F', amount: '1,490', code: 'THB' },
    },
  },
  {
    name: 'Professional', price: '99', tagline: 'For growing properties', popular: true,
    features: ['Up to 100 rooms', 'Advanced analytics', 'Priority support', '10 users', 'Custom reports', 'Channel manager', 'POS integration', 'AI dynamic pricing', 'AI review responses', 'AI guest concierge'],
    regionalPricing: {
      USD: { symbol: '$', amount: '99', code: 'USD' }, EUR: { symbol: '\u20AC', amount: '89', code: 'EUR' },
      GBP: { symbol: '\u00A3', amount: '79', code: 'GBP' }, AED: { symbol: 'AED', amount: '349', code: 'AED' },
      INR: { symbol: '\u20B9', amount: '7,999', code: 'INR' }, THB: { symbol: '\u0E3F', amount: '2,990', code: 'THB' },
    },
  },
  {
    name: 'Enterprise', price: '199', tagline: 'For hotel chains',
    features: ['Unlimited rooms', 'Full AI suite (25 features)', '24/7 phone support', 'Unlimited users', 'All integrations', 'Multi-property', 'White label', 'API access'],
    regionalPricing: {
      USD: { symbol: '$', amount: '199', code: 'USD' }, EUR: { symbol: '\u20AC', amount: '179', code: 'EUR' },
      GBP: { symbol: '\u00A3', amount: '159', code: 'GBP' }, AED: { symbol: 'AED', amount: '699', code: 'AED' },
      INR: { symbol: '\u20B9', amount: '15,999', code: 'INR' }, THB: { symbol: '\u0E3F', amount: '5,990', code: 'THB' },
    },
  },
];

/* ── Property Type Filter Options ────────────────────────────────────── */

export const PROPERTY_TYPES = ['Hotel', 'Resort', 'Boutique', 'Heritage'] as const;

/* ── India Outline SVG Path (simplified) ─────────────────────────────── */

export const INDIA_OUTLINE_PATH =
  'M 318,523 L 276,488 L 228,433 L 198,388 L 161,317 L 161,278 ' +
  'L 33,230 L 67,168 L 210,84 L 233,28 L 317,19 L 367,103 ' +
  'L 533,140 L 683,159 L 933,140 L 833,184 L 833,243 ' +
  'L 683,225 L 679,252 L 594,303 L 507,343 L 409,429 L 394,451 Z';
